import React, { useMemo, useState } from 'react';
import {
  SelectChangeEvent,
  Select,
  MenuItem,
  Box,
  InputLabel,
  FormControl,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SvgIconComponent } from '@mui/icons-material';

interface CustomDropdownFieldProps {
  dropdownOptions: {
    id: number | string;
    label: string;
    icon?: string | React.ElementType;
    iconPosition?: string;
    style?: React.CSSProperties;
  }[];
  label?: string;
  handleChange: (event: SelectChangeEvent) => void;
  selectedOption: string;
  icon?: SvgIconComponent;
  wrapperSx?: Object;
  MenuProps?: Object;
  disabled?: boolean;
  isAddNew?: boolean;
  onAddNewClick?: () => void;
  addNewLabel?: string;
  isLoading?: boolean;
  showSearch?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownFieldProps> = ({
  dropdownOptions,
  handleChange,
  selectedOption,
  icon,
  wrapperSx,
  MenuProps,
  disabled,
  label,
  isAddNew,
  onAddNewClick,
  addNewLabel,
  isLoading,
  showSearch = false,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const menuProps = MenuProps as Record<string, any> | undefined;
  const mergedMenuProps = {
    ...menuProps,
    PaperProps: {
      ...menuProps?.PaperProps,
      sx: {
        ...(isAddNew && { overflow: 'hidden' }),
        ...menuProps?.PaperProps?.sx,
      },
    },
    MenuListProps: {
      ...menuProps?.MenuListProps,
      sx: {
        ...(isAddNew && {
          maxHeight: 300,
          overflowY: 'auto',
          paddingBottom: 0,
        }),
        ...menuProps?.MenuListProps?.sx,
      },
    },
  };

  const filteredOptions = useMemo(() => {
    if (!showSearch || !searchValue.trim()) return dropdownOptions;

    const searchLower = searchValue.toLowerCase();
    return dropdownOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        String(option.id) === String(selectedOption),
    );
  }, [dropdownOptions, searchValue, showSearch, selectedOption]);
  return (
    <FormControl>
      {label && (
        <InputLabel
          id={`${label}-label`}
          sx={{
            fontSize: '12px',
            color: '#6C6C6C',
            fontWeight: 500,
            backgroundColor: '#FFFFFF',
            marginTop: '2px',
            paddingX: '5px',
          }}
        >
          {label}
        </InputLabel>
      )}

      <Select
        label={label}
        labelId={`${label}-label`}
        onChange={handleChange}
        onClose={() => setSearchValue('')}
        value={selectedOption || ''}
        IconComponent={
          isLoading
            ? () => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                  }}
                >
                  <CircularProgress size={18} sx={{ color: '#162936', marginRight: '0.25rem' }} />
                </div>
              )
            : icon
              ? icon
              : ArrowDropDownIcon
        }
        MenuProps={isAddNew ? mergedMenuProps : MenuProps}
        sx={{
          ...{
            boxShadow: 'none',
            '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
            '& .MuiSelect-select': {
              padding: 0,
            },
            '.MuiSvgIcon-root ': {
              fill: '#2F736E !important',
            },
            borderRadius: '8px',
            border: '1px solid #E8EAEB',
            paddingY: '0.375rem',
            paddingLeft: '1rem',
            paddingRight: '0.75rem',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            letterSpacing: '-0.35px',
            fontWeight: 700,
            color: '#6C6C6C',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '48px',
          },
          '.MuiSvgIcon-root ': {
            fill: '#2F736E !important',
          },
          borderRadius: '8px',
          border: '1px solid #E8EAEB',
          paddingY: '0.375rem',
          paddingLeft: '1rem',
          paddingRight: '0.75rem',
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          letterSpacing: '-0.35px',
          fontWeight: 700,
          color: '#6C6C6C',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '48px',
          ...(icon && {
            '.MuiSelect-icon': {
              top: 'auto',
              alignItems: 'center',
              transform: 'none',
            },
          }),

          ...wrapperSx,
        }}
        disabled={disabled || isLoading}
      >
        {showSearch && (
          <TextField
            label={label}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            disabled={disabled || isLoading}
            placeholder="Search for a scenario"
            sx={{
              marginX: '8px',
              marginBottom: '4px',
              '& .MuiInputBase-input': {
                padding: '8px',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none',
              },
            }}
          />
        )}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <MenuItem
              key={option.id}
              value={option.id}
              sx={{
                display: 'flex',
                gap: '4px',
                padding: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#162936',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ...option.style }}>
                {option.iconPosition === 'start' &&
                  option.icon &&
                  (typeof option.icon === 'string' ? (
                    <img src={option.icon} alt={option.label} />
                  ) : (
                    React.createElement(option.icon, { style: option.style })
                  ))}
                {option.label}
                {option.iconPosition === 'end' &&
                  option.icon &&
                  (typeof option.icon === 'string' ? (
                    <img src={option.icon} alt={option.label} />
                  ) : (
                    React.createElement(option.icon, { style: option.style })
                  ))}
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              paddingY: '12px',
              paddingX: '16px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#162936',
              width: '100%',
            }}
          >
            <Typography sx={{ color: '#6C6C6C', fontSize: '12px', fontWeight: 500 }}>
              No results found
            </Typography>
          </MenuItem>
        )}
        {isAddNew && (
          <MenuItem
            sx={{
              color: '#51B8B0',
              borderTop: '1px solid #E8EAEB',
              position: 'sticky',
              bottom: 0,
              backgroundColor: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#FFFFFF',
              },
              fontWeight: 700,
              fontSize: '14px',
              zIndex: 1,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onAddNewClick?.();
            }}
          >
            {addNewLabel}
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};
export default CustomDropdown;
